import { faChevronLeft, faChevronRight } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react'
import { s3 } from '~/utils/s3'
import { SectionHeading, SectionSubheading } from './SectionHeading'

/**
 * One object per slide rather than three parallel arrays indexed in lockstep. Adding a fifth video
 * to the old shape meant editing three lists and getting the order right in all of them.
 */
const slides = [
    {
        video: s3('/video/spawn-kill-earn-repeat'),
        title: 'SPAWN, KILL, EARN, REPEAT',
        subtitle: 'TAKE OTHER PLAYERS MONEY WITH EVERY KILL IN AN INFINITE GAME MODE',
    },
    {
        video: s3('/video/claim-cash-drops'),
        title: 'CLAIM CASH DROPS',
        subtitle: 'LOOT THE MAP TO FIND HIDDEN CASH DROPS + EXTRACT TO SECURE YOUR DROPS.',
    },
    {
        video: s3('/video/skill-pays'),
        title: 'SKILL PAYS',
        subtitle: 'THE BETTER YOUR K/D RATIO, THE MORE YOU’LL EARN.',
    },
    {
        video: s3('/video/choose-your-spawn-fee'),
        title: 'CHOOSE YOUR SPAWN FEE',
        subtitle: 'PAY $0.10, $0.25, $1 or $5 PER SPAWN, AND EARN THAT MUCH PER KILL.',
    },
]

/**
 * A bordered square, the same object as the social buttons in the footer.
 *
 * 44px because that is the smallest reliable thumb target, and bordered because the control it
 * replaced — a 12px square with no outline — did not read as pressable at all.
 */
const ArrowButton = ({ label, icon, onClick }: { label: string; icon: typeof faChevronLeft; onClick: () => void }) => (
    <button
        type='button'
        aria-label={label}
        onClick={onClick}
        className='flex h-11 w-11 shrink-0 items-center justify-center border border-white/25 text-base transition-colors hover:border-white/60 hover:bg-white/5'
    >
        <FontAwesomeIcon icon={icon} />
    </button>
)

/**
 * Four videos, one at a time.
 *
 * The navigation is the point of this rewrite. It was four 12px squares under the video, which is a
 * position indicator being asked to do the job of a control: nothing about it said "there are three
 * more of these" or "you can move between them", and on a phone the only discoverable way through
 * was to guess that the strip swiped.
 *
 * So the indicator says how many there are and how far along you are — four wide segments, the
 * current one lit in the brand gradient — and it is flanked by two arrows that say the thing the
 * dots never did: this moves. The segments stay clickable, the strip still swipes, and the arrows
 * wrap at both ends so neither one is ever a dead control.
 */
export function GameplayVideo() {
    const [current, setCurrent] = useState(0)
    const [muted, setMuted] = useState(true)
    const scrollRef = useRef<HTMLDivElement>(null)
    const videoRefs = useRef<(HTMLVideoElement | null)[]>([])
    const isScrollingRef = useRef(false)
    const scrollEndTimer = useRef(null)

    /*
      `current` is normally owned by the IntersectionObserver below, so that swiping updates it. A
      programmatic jump has to suppress that while the smooth scroll is in flight, or every slide it
      passes over crosses the 50% threshold and the indicator counts up through them.

      The old suppression was a flat 100ms, which is shorter than the scroll it was covering. This
      one ends on `scrollend` where the browser has it, with a timer as the fallback — and the timer
      is reset on every call, so mashing an arrow cannot leave the flag stuck on.
    */
    const goTo = useCallback((index: number) => {
        const container = scrollRef.current
        const target = (index + slides.length) % slides.length

        setCurrent(target)
        if (!container) return

        isScrollingRef.current = true
        clearTimeout(scrollEndTimer.current)
        scrollEndTimer.current = setTimeout(() => {
            isScrollingRef.current = false
        }, 1000)

        container.scrollTo({ left: target * container.offsetWidth, behavior: 'smooth' })
    }, [])

    useEffect(() => {
        const container = scrollRef.current
        if (!container) return
        const onScrollEnd = () => {
            isScrollingRef.current = false
        }
        container.addEventListener('scrollend', onScrollEnd)
        return () => {
            container.removeEventListener('scrollend', onScrollEnd)
            clearTimeout(scrollEndTimer.current)
        }
    }, [])

    useEffect(() => {
        videoRefs.current.forEach((video, i) => {
            if (!video) return
            // The rejection is swallowed on purpose. `play()` resolves asynchronously, so moving on
            // before it settles rejects it with "interrupted by a call to pause()" — which is now
            // easy to do, because two arrows make stepping through four videos quick. Nothing is
            // wrong when it happens, and unhandled it fills the console.
            if (i === current) video.play().catch(() => {})
            else video.pause()
        })
    }, [current])

    useLayoutEffect(() => {
        const observer = new IntersectionObserver(
            (entries) => {
                if (isScrollingRef.current) return
                entries.forEach((entry) => {
                    if (entry.isIntersecting) {
                        setCurrent(videoRefs.current.indexOf(entry.target as HTMLVideoElement))
                    }
                })
            },
            { root: scrollRef.current, threshold: 0.5 }
        )
        videoRefs.current.forEach((video) => {
            if (video) observer.observe(video)
        })
        return () => observer.disconnect()
    }, [])

    return (
        <div id='game_mechanics' className='flex flex-col items-center py-8'>
            {/* Arrow keys work once anything in here has focus, which is what makes the arrows
                usable without a mouse and costs one handler. */}
            <div
                className='w-full'
                onKeyDown={(e) => {
                    if (e.key === 'ArrowLeft') goTo(current - 1)
                    if (e.key === 'ArrowRight') goTo(current + 1)
                }}
            >
                <div
                    ref={scrollRef}
                    className='no-scrollbar flex w-full max-w-full snap-x snap-mandatory flex-row overflow-y-hidden overflow-x-auto scroll-smooth'
                >
                    {slides.map((slide, i) => (
                        <div
                            key={slide.title}
                            className='flex w-full min-w-full snap-center flex-col items-center'
                            aria-hidden={current !== i}
                        >
                            <div className='flex flex-col gap-2'>
                                <SectionHeading>{slide.title}</SectionHeading>
                                <SectionSubheading>{slide.subtitle}</SectionSubheading>
                            </div>
                            <div style={{ aspectRatio: '1349/754' }} className='my-6 w-full max-w-[92%] md:max-w-[65%]'>
                                <video
                                    ref={(el) => {
                                        videoRefs.current[i] = el
                                    }}
                                    height={754}
                                    width={1349}
                                    controls={false}
                                    muted={muted}
                                    playsInline
                                    loop
                                    preload='none'
                                    className='h-auto w-full object-contain'
                                    onClick={() => setMuted((prev) => !prev)}
                                >
                                    <source
                                        src={`${slide.video}.1080.mp4`}
                                        type='video/mp4'
                                        media='(min-width: 1920px)'
                                    />
                                    <source src={`${slide.video}.720.mp4`} type='video/mp4' />
                                </video>
                            </div>
                        </div>
                    ))}
                </div>

                {/*
                  `tablist` is the accurate role: four controls, exactly one selected, each
                  revealing its own panel. It is also what gives a screen reader the "3 of 4" the
                  segments give everyone else.
                */}
                <div id='video-controls' className='flex flex-row items-center justify-center gap-3 px-4'>
                    <ArrowButton label='Previous video' icon={faChevronLeft} onClick={() => goTo(current - 1)} />
                    <div role='tablist' aria-label='Game mechanics' className='flex flex-row items-center gap-2'>
                        {slides.map((slide, i) => (
                            // The bar is 4px tall; the padding around it is what the thumb hits.
                            <button
                                key={slide.title}
                                type='button'
                                role='tab'
                                aria-selected={current === i}
                                aria-label={slide.title}
                                onClick={() => goTo(i)}
                                className='group flex items-center justify-center py-4'
                            >
                                <span
                                    className={`block h-1 w-8 transition-colors md:w-12 ${
                                        current === i
                                            ? 'bg-[linear-gradient(90deg,#03CEFF_0%,#C91FFF_100%)]'
                                            : 'bg-white/25 group-hover:bg-white/50'
                                    }`}
                                />
                            </button>
                        ))}
                    </div>
                    <ArrowButton label='Next video' icon={faChevronRight} onClick={() => goTo(current + 1)} />
                </div>
            </div>
        </div>
    )
}
